import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import { useLanguage } from '@/core/i18n/LanguageContext';
import { credentialLogin, labTokenLogin } from '@/modules/auth/api/authApi';
import { beginOidcLogin, getAuthMode } from '@/modules/auth/oidc/pkce';
import { getPlatformLabToken } from '@/core/api';
import { isAccessTokenExpired } from '@/core/auth/sessionExpiry';

function loginFailureText(err, t) {
  const raw = String(err?.message || '');
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.code === 'unauthorized' || parsed?.message === 'invalid token') return t('errorLogin');
    if (parsed?.message) return String(parsed.message);
  } catch { /* plain text from the IdP */ }
  if (raw.trim().startsWith('{')) return t('errorLogin');
  return raw || t('errorLogin');
}

const REMEMBER_KEY = 'ops-login-remember';
const REMEMBER_USER_KEY = 'ops-login-username';
const publicUrl = process.env.PUBLIC_URL || '';

const IconUser = () => (
  <svg className="lead" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconLock = () => (
  <svg className="lead" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const IconEye = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.9 17.9A10.9 10.9 0 0 1 12 19c-6.5 0-10-7-10-7a18.6 18.6 0 0 1 5.1-5.9M9.9 5.2A10 10 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.2 3.2M1 1l22 22" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

/**
 * Login: username/email + password (IdP), SSO (PKCE), optional lab HMAC for dual/lab.
 * Visuals match ProxNova-SOLAR-Light-Dark desktop design.
 */
const LoginPage = () => {
  const { language, setLanguage, t } = useLanguage();
  const [theme, setTheme] = useState('light');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [labToken, setLabToken] = useState('');
  const [showLabToken, setShowLabToken] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user, token, loading: authLoading } = useAuth();
  const mode = getAuthMode();
  const showOidc = mode === 'oidc' || mode === 'dual';
  const showLab = mode === 'lab' || mode === 'dual';
  const showPasswordForm = showOidc;

  useEffect(() => {
    if (authLoading) return;
    if (user && token && !isAccessTokenExpired(token)) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, token, navigate]);

  useEffect(() => {
    const savedTheme = (() => {
      try {
        const cur = localStorage.getItem('ops-theme');
        if (cur != null) return cur;
        const legacy = localStorage.getItem('dio-theme');
        if (legacy != null) {
          localStorage.setItem('ops-theme', legacy);
          return legacy;
        }
      } catch { /* ignore */ }
      return null;
    })();
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme ? savedTheme : (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    document.documentElement.classList.toggle('dark', initialTheme === 'dark');
    document.documentElement.dataset.theme = initialTheme === 'light' ? 'light' : 'dark';

    try {
      const remembered = localStorage.getItem(REMEMBER_KEY) === '1';
      setRemember(remembered);
      if (remembered) {
        const savedUser = localStorage.getItem(REMEMBER_USER_KEY) || '';
        if (savedUser) setUsername(savedUser);
      }
    } catch { /* ignore */ }

    const envTok = getPlatformLabToken();
    if (envTok) {
      setLabToken(envTok);
      setShowLabToken(true);
    }
  }, []);

  const applyTheme = (next) => {
    setTheme(next);
    localStorage.setItem('ops-theme', next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    document.documentElement.dataset.theme = next === 'light' ? 'light' : 'dark';
  };

  const toggleTheme = () => applyTheme(theme === 'dark' ? 'light' : 'dark');

  const persistRemember = (userName) => {
    try {
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, '1');
        localStorage.setItem(REMEMBER_USER_KEY, userName || '');
      } else {
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem(REMEMBER_USER_KEY);
      }
    } catch { /* ignore */ }
  };

  const handleOidc = async () => {
    setError('');
    setLoading(true);
    try {
      await beginOidcLogin();
    } catch (err) {
      setError(err.message || t('errorLogin'));
      setLoading(false);
    }
  };

  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { access_token, user: authUser } = await credentialLogin(username, password);
      persistRemember(username);
      login(access_token, authUser);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Credential login error:', err);
      const isNetwork = !err.response || (err.message && String(err.message).toLowerCase().includes('network'));
      setError(isNetwork ? t('errorNetwork') : loginFailureText(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleLabSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { access_token, user: authUser } = await labTokenLogin(labToken);
      login(access_token, authUser);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Lab login error:', err);
      const isNetwork = !err.response || (err.message && String(err.message).toLowerCase().includes('network'));
      setError(isNetwork ? t('errorNetwork') : (err.message || t('errorLogin')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="page">
        <div className="scene" aria-hidden="true" />
        <div className="xart" aria-hidden="true" />

        <main className="card" role="region" aria-label="Login">
          <div className="card-head">
            <div className="logo" aria-label="ProxNova">
              <img
                className="neg"
                src={`${publicUrl}/proxnova/prox-nova-logo.svg`}
                alt="ProxNova"
              />
              <img
                className="pos"
                src={`${publicUrl}/proxnova/prox-nova-logo-light.svg`}
                alt="ProxNova"
              />
              <span className="scada-tag">SCADA</span>
            </div>
            <div className="head-ctls">
              <button
                type="button"
                onClick={() => setLanguage('sq')}
                title="Shqip"
                className={`login-lang ${language === 'sq' ? 'is-on' : ''}`}
              >
                SQ
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                title="English"
                className={`login-lang ${language === 'en' ? 'is-on' : ''}`}
              >
                EN
              </button>
              <button
                type="button"
                className="theme-btn"
                onClick={toggleTheme}
                aria-label={t('toggleThemeLabel')}
              >
                {theme === 'dark' ? '☀' : '☾'}
              </button>
            </div>
          </div>

          <h1 className="title">{t('loginTitle')}</h1>
          <p className="sub subtitle">{t('loginSubtitle')}</p>

          {error && (
            <div className="errors">
              <ul>
                <li>{error}</li>
              </ul>
            </div>
          )}

          {showPasswordForm && (
            <form onSubmit={handleCredentialSubmit} autoComplete="on" id="dioCredentialLoginForm" noValidate>
              <div className="field">
                <label htmlFor="username">{t('usernameOrEmailLabel')}</label>
                <div className="input">
                  <IconUser />
                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder={t('usernameOrEmailLabel')}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="password">{t('passwordLabel')}</label>
                <div className="input">
                  <IconLock />
                  <input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder={t('passwordLabel')}
                  />
                  <button
                    type="button"
                    className="eye"
                    onClick={() => setShowPass((v) => !v)}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>
              <label className="remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className="box"><IconCheck /></span>
                {t('rememberCreds')}
              </label>
              <button className="btn" type="submit" disabled={loading}>
                {loading ? <span className="spin" aria-hidden="true" /> : null}
                <span>{loading ? t('loggingIn') : t('loginBtn')}</span>
              </button>
            </form>
          )}

          {showOidc && (
            <>
              {showPasswordForm && <p className="or-line">{t('orContinueWith')}</p>}
              <button className="btn btn-ghost" type="button" disabled={loading} onClick={handleOidc}>
                {loading ? t('loggingIn') : t('signInWithSso')}
              </button>
            </>
          )}
        </main>
        <div className="foot">© {new Date().getFullYear()} ProxNova SCADA</div>
      </div>
    </div>
  );
};

export default LoginPage;
